import React from 'react';
import { Bell, Star, Info } from 'lucide-react';
import Dropdown from './Dropdown';

/**
 * WebhookSelector Component
 * 
 * Reusable component for selecting webhook notifications with disable option.
 * Used in both bulk execution and scheduled test configurations.
 * 
 * Industry Pattern: Consistent form controls across application
 * Similar to: AWS notification preferences, GitHub Actions secrets selector
 * 
 * @example
 * ```tsx
 * <WebhookSelector
 *   webhooks={availableWebhooks}
 *   selectedWebhookId={webhookId}
 *   onWebhookChange={setWebhookId}
 *   disableNotifications={disabled}
 *   onDisableChange={setDisabled}
 *   loading={isLoading}
 * />
 * ```
 */

interface WebhookSelectorProps {
  /** Array of available webhooks */
  webhooks: Array<{
    id: string;
    name: string;
    isDefault?: boolean;
    isActive?: boolean;
  }>;
  /** Currently selected webhook ID */
  selectedWebhookId: string;
  /** Callback when webhook selection changes */
  onWebhookChange: (webhookId: string) => void;
  /** Whether notifications are disabled */
  disableNotifications: boolean;
  /** Callback when disable notifications changes */
  onDisableChange: (disabled: boolean) => void;
  /** Loading state for webhooks */
  loading?: boolean;
  /** Additional CSS classes */
  className?: string;
}

export const WebhookSelector: React.FC<WebhookSelectorProps> = ({
  webhooks,
  selectedWebhookId,
  onWebhookChange,
  disableNotifications,
  onDisableChange,
  loading = false,
  className = ''
}) => {
  const defaultWebhook = webhooks.find(w => w.isDefault);

  return (
    <div className={`space-y-3 ${className}`}>
      {/* Webhook Dropdown */}
      <div>
        <div className="flex items-center gap-1.5 mb-2">
          <label className="text-xs font-medium text-gray-600 dark:text-slate-400">
            Send Notifications To
          </label>
          <div className="group relative inline-flex">
            <Info className="w-3.5 h-3.5 text-gray-400 hover:text-gray-600 dark:hover:text-slate-300 cursor-help transition-colors" />
            {/* Tooltip - positioned below icon to avoid cutoff */}
            <div 
              className="absolute left-1/2 top-full mt-2 w-56 px-3 py-2.5 bg-gray-900 dark:bg-slate-700 text-white text-xs rounded-lg shadow-xl opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200 pointer-events-none z-[100]"
              style={{ transform: 'translateX(-50%)' }}
            >
              <div className="relative">
                <p className="leading-relaxed">
                  Select a specific webhook or leave as default. If not selected, notifications will be sent to the default webhook.
                </p>
                {/* Tooltip arrow pointing up */}
                <div 
                  className="absolute -top-1 left-1/2 w-2 h-2 bg-gray-900 dark:bg-slate-700 transform rotate-45"
                  style={{ marginLeft: '-4px' }}
                />
              </div>
            </div>
          </div>
        </div>
        <Dropdown
          options={[
            { 
              value: '', 
              label: defaultWebhook?.name || 'Default webhook',
              icon: <Star className="h-4 w-4 text-yellow-500 fill-current" />
            },
            ...webhooks
              .filter(w => !w.isDefault)
              .map(webhook => ({
                value: webhook.id,
                label: webhook.name,
                disabled: !webhook.isActive,
                icon: <Bell className="h-4 w-4" />
              }))
          ]}
          value={selectedWebhookId}
          onChange={(value) => onWebhookChange(value as string)}
          loading={loading}
          disabled={webhooks.length === 0}
          icon={<Bell className="h-4 w-4 text-blue-600" />}
          className="text-xs"
        />
        {webhooks.length === 0 && !loading && (
          <p className="text-xs text-gray-500 dark:text-slate-400 mt-1.5">
            No webhooks configured. Add webhooks in Settings → Webhooks.
          </p>
        )}
      </div>
      
      {/* Disable Notifications Checkbox */}
      <label className="flex items-start gap-2.5 cursor-pointer p-2.5 rounded-lg border border-gray-200 dark:border-slate-700 hover:bg-white dark:hover:bg-slate-700/50 transition-colors">
        <input
          type="checkbox"
          checked={disableNotifications}
          onChange={(e) => onDisableChange(e.target.checked)}
          className="mt-0.5 rounded text-blue-600 focus:ring-blue-500 focus:ring-2 cursor-pointer"
        />
        <div className="flex-1 min-w-0">
          <span className="text-xs font-medium text-gray-700 dark:text-slate-300 block">
            Don't send notifications for this execution
          </span>
          <p className="text-xs text-gray-500 dark:text-slate-400 mt-0.5 leading-relaxed">
            Tests will run normally, but no webhook notifications will be triggered
          </p>
        </div>
      </label>
    </div>
  );
};

export default WebhookSelector;

